import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Modal, message, Space, Tooltip, Radio, Typography } from 'antd';
import { FlexFill, FooterWrapper, RollingWindowWrapper, Title } from './styled';
import { tid } from '@utils/test';
import useSaveDashboard from '../useSaveDashboard';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { parseDashboardURL } from '@utils';
import changeSaveDashboardPromptVisibility from '@store/dashboards/ui/actions';
import { createSavedDashboard, updatedSavedDashboard } from '@store/tempDashboard/actions';
import useUpdateDashboard from '../useUpdateDashboard';
import { useQueryClient } from 'react-query';
import { SAVED_DASHBOARDS_BASE_KEY } from '@utils/constants';
import track, {
  CANCEL_SAVE_DASHBOARD_POPUP,
  CANCEL_UPDATE_DASHBOARD_POPUP,
  CLICK_ON_PRIVATE_PUBLIC_CHECKBOX,
} from '@src/utils/sensor/track';
import { SavedDataInterface } from '../useSaveSet';
import useGetDataToBeSaved from '../useGetDataToBeSaved';
import { get, set } from 'lodash';
import { extractIntervalFromConfig } from '@utils/dashboard/rollingWindow';

const { Text } = Typography;

function SaveDashboardPopup({
  isPopupOpen,
  closePopup,
  pageMeta,
}: {
  isPopupOpen: boolean;
  closePopup: () => void;
  pageMeta;
}) {
  const { saveMutation } = useSaveDashboard();
  const { updateMutation } = useUpdateDashboard();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    // Setting default as a null check, to pass scenarios where
    // multiple saved dashboard tabs are open,
    // user goes to Trends and reloads. Trends fails to render otherwise.
    // (since dashboard tabs are child components, which load this popup)
    //
    // React query does not fetch the data for the saved dashboard
    // unless the use has that particular tab open.
    //
    // But the popup is loaded and can be opened even if the user is on some other tab
    // and clicks on the close tab button.
    dataToBeSaved = {
      meta: {},
      config: {},
      initialSavedData: {},
    },
  }: {
    dataToBeSaved: SavedDataInterface;
  } = useGetDataToBeSaved({ pageMeta });

  const { meta, config, initialSavedData } = dataToBeSaved;
  const { id, name, isPublic, isSaved, dashboardTypeId } = meta;

  const [dashboardName, setDashboardName] = useState(name || '');
  const [publicChecked, setPublicChecked] = useState(isPublic || false);

  // initail state is from current saved object(db)
  const [rollingWindow, setRollingWindow] = useState(
    get(initialSavedData, ['config', 'meta', 'rollingWindow'], true),
  );

  // add rolling window state to config object
  set(config, 'meta', { rollingWindow });

  const onNameChange = (event) => {
    setDashboardName(event.target.value);
  };

  const togglePublic = (event) => {
    setPublicChecked(event.target.checked);
    track({ event: CLICK_ON_PRIVATE_PUBLIC_CHECKBOX });
  };

  const toggleRollingWindow = (event) => {
    setRollingWindow(event.target.value);
  };

  const onCancelPopup = () => {
    closePopup();
    track({
      event: isSaved ? CANCEL_UPDATE_DASHBOARD_POPUP : CANCEL_SAVE_DASHBOARD_POPUP,
    });
  };

  const callSaveHandler = async () => {
    try {
      const { data } = await saveMutation.mutateAsync({
        ...meta,
        config,
        name: dashboardName,
        isPublic: publicChecked,
        isSaved: true,
      });

      closePopup();

      // sd-todo: check here if the tab behaviour causes issues
      const tab = {
        id: data.id,
        ...meta,
        config,
      };

      // sd-TODO: might need to enable this for shared dashboards, to remove them when a saved dashboard is created
      // dispatch(triggerReplaceTab(tab, unsavedDashboardId, data.id)),
      // dispatch(getList());

      // sd-TODO: create a new action which just sets the active dashboard id from below action
      dispatch(changeSaveDashboardPromptVisibility(false, tab.id));
      dispatch(push(parseDashboardURL(tab)));
      dispatch(createSavedDashboard(data.id, data.dashboard, dashboardTypeId, config));
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const callUpdateHandler = async () => {
    try {
      const { data } = await updateMutation.mutateAsync({
        id,
        payload: {
          ...meta,
          config,
          name: dashboardName,
          isPublic: publicChecked,
        },
      });

      queryClient.invalidateQueries([SAVED_DASHBOARDS_BASE_KEY, id]);

      closePopup();
      dispatch(updatedSavedDashboard(data.id, dashboardName));
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const showRollingWindowOption = get(pageMeta, ['showRollingWindowOption'], false);

  const modalTitle = <Title>{isSaved ? 'Update Dashboard' : 'Save Dashboard'}</Title>;

  const footerButtons = [
    <FooterWrapper key="sd-popup-footer-wrapper" {...tid('sd-popup-footer')}>
      <Checkbox checked={publicChecked} onChange={togglePublic} disabled={isSaved}>
        Make it Public
      </Checkbox>
      <FlexFill />
      <Button key="sd-popup-cancel" onClick={closePopup}>
        Cancel
      </Button>
      {isSaved ? (
        <Button
          type="primary"
          key="sd-popup-update"
          disabled={dashboardName ? false : true}
          onClick={callUpdateHandler}
        >
          Update
        </Button>
      ) : (
        <Button
          type="primary"
          key="sd-popup-save"
          disabled={dashboardName ? false : true}
          onClick={callSaveHandler}
        >
          Save
        </Button>
      )}
    </FooterWrapper>,
  ];

  return (
    <Modal
      title={modalTitle}
      width={400}
      open={isPopupOpen}
      okText="Save"
      cancelText="Cancel"
      footer={footerButtons}
      maskClosable={true}
      onCancel={onCancelPopup}
    >
      <Space direction="vertical">
        <Input value={dashboardName} placeholder="Dashboard Name" onChange={onNameChange} />
        {showRollingWindowOption && (
          <Space direction="vertical">
            <RollingWindowWrapper>
              <span>Time-Window</span>
              <Radio.Group value={rollingWindow} buttonStyle="solid" onChange={toggleRollingWindow}>
                <Radio.Button value={true}>
                  <Tooltip title="Save dashboard with rolling time-window (ex. Last 24 hours) based on the calendar shortcut selected">
                    Rolling
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={false}>Fixed</Radio.Button>
              </Radio.Group>
            </RollingWindowWrapper>
            <Text type="secondary">
              {extractIntervalFromConfig({ pageMeta, config, rollingWindow })}
            </Text>
          </Space>
        )}
      </Space>
    </Modal>
  );
}

export default SaveDashboardPopup;
